import * as THREE from 'three';
import { Socket } from 'socket.io-client';
import { PostmanController } from './PostmanController';

// Mini Motorways style colors
const PACKAGE_COLORS = [
  0xff5a5a,  // Red
  0x3498ff,  // Blue
  0xffe234,  // Yellow
  0xff9e2d,  // Orange
  0x6ebf4b,  // Green
];

interface Package {
  id: string;
  mesh: THREE.Group;
  position: THREE.Vector3;
  isPickedUp: boolean;
  endPosition: THREE.Vector3;
  value: number;
  color: number;
  pickedUpBy?: string;
  pickupTime?: number; // Timestamp when the package was picked up
  createdAt?: number; // Timestamp when the package was created
  labelElement?: HTMLElement; // Add reference to the label element
  destinationLabelElement?: HTMLElement; // Add reference to the destination label element
  isBeingDelivered: boolean;
  isStolen: boolean;
}

interface DeliveryPoint {
  mesh: THREE.Group;
  position: THREE.Vector3;
  id: string;
  color: number;
}

export class PackageManager {
  private scene: THREE.Scene;
  private socket: Socket;
  private postmanController: PostmanController;
  private packages: Map<string, Package> = new Map();
  private deliveryPoints: Map<string, DeliveryPoint> = new Map();
  private pickupRange: number = 2;
  private deliveryRange: number = 2;
  private citySize: number = 100;
  private deliveredPackages: number = 0;
  private scoreElement: HTMLElement;
  private confidenceBarElement: HTMLElement; // New confidence bar element
  private inventoryElement: HTMLElement;
  private inventoryUpdateInterval: number | null = null;
  private camera: THREE.Camera;
  private packageLabelsContainer: HTMLElement;
  private lastCameraPosition: THREE.Vector3 = new THREE.Vector3();
  private lastCameraRotation: THREE.Euler = new THREE.Euler();
  private updateThreshold: number = 0.05;
  private playerScore: number = 0; // Suivi du score du joueur
  private playerConfidence: number = 1; // Player's confidence level, initialized to 1
  private leaderboardElement: HTMLElement; // Element for the leaderboard

  constructor(scene: THREE.Scene, socket: Socket, postmanController: PostmanController, camera: THREE.Camera) {
    this.scene = scene;
    this.socket = socket;
    this.postmanController = postmanController;
    this.camera = camera;
    
    // Create score and inventory elements
    this.scoreElement = this.createScoreElement();
    this.confidenceBarElement = this.createConfidenceBarElement();
    this.inventoryElement = this.createInventoryElement();
    this.leaderboardElement = document.getElementById('leaderboard') || this.createLeaderboardElement();
    
    // Create or get the package labels container
    this.packageLabelsContainer = document.getElementById('package-labels') || this.createPackageLabelsContainer();
    
    // Initialize camera tracking for label positioning
    this.lastCameraPosition.copy(camera.position);
    this.lastCameraRotation.copy(camera.rotation);
    
    this.setupSocketListeners();
    
    // Start inventory update interval
    this.startInventoryUpdate();

    // Écouter la demande de vérification des colis volés
    this.socket.on('checkStolenPackages', (callback) => {
      const hasStolenPackages = this.hasStolenPackages();
      callback(hasStolenPackages);
    });
  }

  private createScoreElement(): HTMLElement {
    // Vérifier si l'élément existe déjà dans le HTML
    const existingScore = document.getElementById('score');
    if (existingScore) {
      // Utiliser l'élément existant
      existingScore.textContent = `$ : ${this.playerScore}`;
      return existingScore;
    }

    // Créer un nouveau score si nécessaire
    const scoreDiv = document.createElement('div');
    scoreDiv.id = 'score';
    scoreDiv.style.position = 'fixed';
    scoreDiv.style.top = '20px';
    scoreDiv.style.right = '20px';
    scoreDiv.style.color = 'white';
    scoreDiv.style.fontFamily = "'Nunito', sans-serif";
    scoreDiv.style.fontSize = '24px';
    scoreDiv.style.fontWeight = '900';
    scoreDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    scoreDiv.style.padding = '10px';
    scoreDiv.style.borderRadius = '5px';
    scoreDiv.style.zIndex = '1000';
    scoreDiv.textContent = `$ : ${this.playerScore}`;
    document.body.appendChild(scoreDiv);
    return scoreDiv;
  }

  private createConfidenceBarElement(): HTMLElement {
    // Supprimer l'ancienne barre de confiance si elle existe
    const existingBar = document.getElementById('confidence-container');
    if (existingBar) {
      existingBar.remove();
    }
    
    // Créer le conteneur
    const confidenceContainer = document.createElement('div');
    confidenceContainer.id = 'confidence-container';
    confidenceContainer.style.position = 'fixed';
    confidenceContainer.style.top = '75px';
    confidenceContainer.style.right = '20px';
    confidenceContainer.style.width = '200px';
    confidenceContainer.style.color = 'white';
    confidenceContainer.style.fontFamily = "'Nunito', sans-serif";
    confidenceContainer.style.fontSize = '14px';
    confidenceContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    confidenceContainer.style.padding = '8px';
    confidenceContainer.style.borderRadius = '5px';
    confidenceContainer.style.zIndex = '1000';
    
    // Appliquer des styles spécifiques pour mobile
    if (this.isMobile()) {
      confidenceContainer.style.width = '150px';
      confidenceContainer.style.padding = '6px';
      confidenceContainer.style.fontSize = '12px';
      confidenceContainer.style.top = '60px';
    }
    
    // Libellé
    const label = document.createElement('div');
    label.textContent = 'CONFIDENCE';
    label.style.marginBottom = '5px';
    confidenceContainer.appendChild(label);
    
    // Conteneur de la barre
    const barContainer = document.createElement('div');
    barContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    barContainer.style.borderRadius = '3px';
    barContainer.style.height = '8px';
    
    // Si mobile, réduire la hauteur de la barre
    if (this.isMobile()) {
      barContainer.style.height = '6px';
    }
    
    // Barre de progression
    const progressBar = document.createElement('div');
    progressBar.id = 'confidence-progress';
    progressBar.style.width = '100%';
    progressBar.style.height = '100%';
    progressBar.style.backgroundColor = '#4CAF50';
    progressBar.style.transition = 'width 0.3s ease, background-color 0.3s ease';
    
    barContainer.appendChild(progressBar);
    confidenceContainer.appendChild(barContainer);
    
    document.body.appendChild(confidenceContainer);
    return confidenceContainer;
  }

  private createInventoryElement(): HTMLElement {
    const inventoryDiv = document.createElement('div');
    inventoryDiv.id = 'inventory';
    inventoryDiv.style.position = 'fixed';
    // Distance entre le haut de confidence (75px) et le haut de l'inventaire
    // La barre de confidence fait environ 37px de hauteur (14px label + 8px barre + 15px padding)
    inventoryDiv.style.top = '130px'; // 75px + 37px + 18px de marge 
    inventoryDiv.style.right = '20px';
    inventoryDiv.style.color = 'white';
    inventoryDiv.style.fontFamily = "'Nunito', sans-serif";
    inventoryDiv.style.fontSize = '16px';
    inventoryDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    inventoryDiv.style.padding = '10px';
    inventoryDiv.style.borderRadius = '5px';
    inventoryDiv.style.minWidth = '200px';
    inventoryDiv.style.maxHeight = '200px';
    inventoryDiv.style.overflowY = 'auto';
    inventoryDiv.style.zIndex = '1000';
    
    // Appliquer des styles spécifiques pour mobile
    if (this.isMobile()) {
      inventoryDiv.style.fontSize = '13px';
      inventoryDiv.style.padding = '6px';
      inventoryDiv.style.minWidth = '150px';
      inventoryDiv.style.top = '105px'; // Ajuster selon la nouvelle taille de la barre de confiance
      inventoryDiv.style.maxHeight = '160px';
    }
    
    document.body.appendChild(inventoryDiv);
    return inventoryDiv;
  }

  private createPackageLabelsContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'package-labels';
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '900'; // Lower than player labels (1000)
    document.body.appendChild(container);
    return container;
  }

  private createLeaderboardElement(): HTMLElement {
    // Create leaderboard container if it doesn't exist
    const leaderboardDiv = document.createElement('div');
    leaderboardDiv.id = 'leaderboard';
    leaderboardDiv.style.position = 'absolute';
    leaderboardDiv.style.top = '20px';
    leaderboardDiv.style.left = '20px';
    leaderboardDiv.style.color = 'white';
    leaderboardDiv.style.fontFamily = "'Nunito', sans-serif";
    leaderboardDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    leaderboardDiv.style.padding = '10px';
    leaderboardDiv.style.borderRadius = '5px';
    leaderboardDiv.style.zIndex = '1000';
    leaderboardDiv.style.minWidth = '200px';
    
    // Add heading
    const heading = document.createElement('h3');
    heading.style.margin = '0 0 10px 0';
    heading.style.fontSize = '18px';
    heading.style.textAlign = 'center';
    heading.style.color = '#FFC107';
    heading.textContent = 'TOP PLAYERS';
    leaderboardDiv.appendChild(heading);
    
    // Add list container
    const listElement = document.createElement('ul');
    listElement.id = 'leaderboard-list';
    listElement.style.listStyleType = 'none';
    listElement.style.padding = '0';
    listElement.style.margin = '0';
    leaderboardDiv.appendChild(listElement);
    
    // Add to document
    document.body.appendChild(leaderboardDiv);
    return leaderboardDiv;
  }

  private updateInventory(): void {
    const inventoryContent = document.createElement('div');
    inventoryContent.style.display = 'flex';
    inventoryContent.style.flexDirection = 'column';
    inventoryContent.style.gap = '8px';

    // Determine if on mobile
    const isMobile = this.isMobile();
    
    // Get all picked up packages by the local player
    const pickedUpPackages = Array.from(this.packages.values())
      .filter(pkg => pkg.isPickedUp && pkg.pickedUpBy === this.socket.id)
      // Sort packages from oldest to newest based on createdAt timestamp
      .sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return a.createdAt - b.createdAt;
      });
      
    if (pickedUpPackages.length === 0) {
      const emptyDiv = document.createElement('div');
      emptyDiv.style.color = '#888';
      emptyDiv.textContent = 'No packages';
      inventoryContent.appendChild(emptyDiv);
    } else {
      pickedUpPackages.forEach((pkg, index) => {
        const packageDiv = document.createElement('div');
        packageDiv.style.display = 'flex';
        packageDiv.style.alignItems = 'center';
        packageDiv.style.gap = isMobile ? '8px' : '12px';
        packageDiv.style.padding = isMobile ? '3px' : '4px';
        
        // Style différent pour les colis volés
        if (pkg.isStolen) {
          packageDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.15)';
          packageDiv.style.borderLeft = '3px solid red';
        } else {
          packageDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }
        
        packageDiv.style.borderRadius = '4px';

        // Package emoji and color indicator
        const packageEmoji = document.createElement('span');
        packageEmoji.textContent = '📦';
        packageEmoji.style.marginRight = '2px';
        packageEmoji.style.fontSize = isMobile ? '16px' : '20px';
        packageDiv.appendChild(packageEmoji);

        // Color indicator
        const colorIndicator = document.createElement('div');
        colorIndicator.style.width = isMobile ? '10px' : '12px';
        colorIndicator.style.height = isMobile ? '10px' : '12px';
        colorIndicator.style.borderRadius = '50%';
        colorIndicator.style.backgroundColor = `#${pkg.color.toString(16).padStart(6, '0')}`;
        packageDiv.appendChild(colorIndicator);
        
        // Value
        const valueDiv = document.createElement('div');
        valueDiv.style.fontWeight = 'bold';
        valueDiv.style.color = '#FFFFFF';
        valueDiv.style.fontSize = isMobile ? '13px' : 'inherit';
        valueDiv.textContent = `$${pkg.value}`;
        packageDiv.appendChild(valueDiv);

        // Indicateur de colis volé
        if (pkg.isStolen) {
          const stolenIndicator = document.createElement('div');
          stolenIndicator.textContent = 'STOLEN';
          stolenIndicator.style.marginLeft = 'auto';
          stolenIndicator.style.color = '#ff5a5a';
          stolenIndicator.style.fontWeight = 'bold';
          stolenIndicator.style.fontSize = isMobile ? '10px' : '12px';
          packageDiv.appendChild(stolenIndicator);
        }
        // Time indicator (seulement pour les colis non volés)
        else if (pkg.pickupTime) {
          const elapsed = (Date.now() - pkg.pickupTime) / 1000; // Convert to seconds
          const remaining = 60 - elapsed; // Total de 60 secondes (30s pour bonus + 30s supplémentaires)
          const isExpired = remaining <= 0;
          const isInBonusTime = elapsed <= 30; // Dans les 30 premières secondes (bonus)
          
          // Calculer la progression sur deux phases
          let progress = 0;
          let color = '#f44336'; // Rouge par défaut
          
          if (elapsed <= 30) {
            // Phase 1: 0-30 secondes (bonus time) - progression de 1 à 0.5
            progress = 1 - (elapsed / 60); // Va de 1 à 0.5 sur 30 secondes
            color = '#4CAF50'; // Vert pour la période de bonus
          } else if (elapsed <= 60) {
            // Phase 2: 30-60 secondes (no bonus) - progression de 0.5 à 0
            progress = (60 - elapsed) / 60; // Va de 0.5 à 0 sur 30 secondes
            color = '#f44336'; // Rouge pour la période sans bonus
          } else {
            // Temps complètement écoulé
            progress = 0;
          }

          const timeContainer = document.createElement('div');
          // Adjust size for mobile
          const svgSize = isMobile ? 30 : 40;
          const circleRadius = isMobile ? 12 : 16;
          const strokeWidth = isMobile ? 3 : 4;
          const circleCenterPoint = svgSize / 2;
          
          timeContainer.style.width = `${svgSize}px`;
          timeContainer.style.height = `${svgSize}px`;
          timeContainer.style.position = 'relative';
          timeContainer.style.marginLeft = 'auto';

          // Create SVG for the crescent
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('width', `${svgSize}`);
          svg.setAttribute('height', `${svgSize}`);
          svg.style.transform = 'rotate(-90deg)';

          // Background circle
          const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          bgCircle.setAttribute('cx', `${circleCenterPoint}`);
          bgCircle.setAttribute('cy', `${circleCenterPoint}`);
          bgCircle.setAttribute('r', `${circleRadius}`);
          bgCircle.setAttribute('fill', 'rgba(255, 255, 255, 0.2)');
          svg.appendChild(bgCircle);

          // Progress circle
          const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          progressCircle.setAttribute('cx', `${circleCenterPoint}`);
          progressCircle.setAttribute('cy', `${circleCenterPoint}`);
          progressCircle.setAttribute('r', `${circleRadius}`);
          progressCircle.setAttribute('fill', 'none');
          progressCircle.setAttribute('stroke', color);
          progressCircle.setAttribute('stroke-width', `${strokeWidth}`);
          progressCircle.setAttribute('stroke-dasharray', `${2 * Math.PI * circleRadius * progress} ${2 * Math.PI * circleRadius}`);
          progressCircle.setAttribute('stroke-linecap', 'round');
          svg.appendChild(progressCircle);

          // Display countdown time instead of symbol
          const textOverlay = document.createElement('div');
          textOverlay.style.position = 'absolute';
          textOverlay.style.top = '50%';
          textOverlay.style.left = '50%';
          textOverlay.style.transform = 'translate(-50%, -50%)';
          textOverlay.style.fontSize = isMobile ? '13px' : '16px';
          textOverlay.style.fontWeight = 'bold';
          textOverlay.style.color = 'black';
          
          // Show the remaining seconds instead of $ or !
          const timeRemaining = isInBonusTime ? Math.ceil(30 - elapsed) : Math.ceil(60 - elapsed);
          textOverlay.textContent = `${Math.max(0, timeRemaining)}`;
          
          timeContainer.appendChild(svg);
          timeContainer.appendChild(textOverlay);
          packageDiv.appendChild(timeContainer);
        }

        inventoryContent.appendChild(packageDiv);
      });
    }

    this.inventoryElement.innerHTML = '';
    this.inventoryElement.appendChild(inventoryContent);
  }

  private setupSocketListeners(): void {
    // Listen for package updates from server
    this.socket.on('packages', (serverPackages: any[]) => {
      // Remove packages that no longer exist on server
      for (const [id, pkg] of this.packages) {
        if (!serverPackages.find(sp => sp.id === id)) {
          this.scene.remove(pkg.mesh);
          // Remove the package label if it exists
          if (pkg.labelElement && pkg.labelElement.parentNode) {
            pkg.labelElement.parentNode.removeChild(pkg.labelElement);
          }
          // Remove the destination label if it exists
          if (pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
            pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
          }
          this.packages.delete(id);
        }
      }

      // Update or create packages
      serverPackages.forEach(serverPackage => {
        if (!this.packages.has(serverPackage.id)) {
          // Create new package
          const position = new THREE.Vector3(
            serverPackage.startPosition.x,
            serverPackage.startPosition.y,
            serverPackage.startPosition.z
          );
          const endPosition = new THREE.Vector3(
            serverPackage.endPosition.x,
            serverPackage.endPosition.y,
            serverPackage.endPosition.z
          );
          const color = PACKAGE_COLORS[Math.floor(Math.random() * PACKAGE_COLORS.length)];
          
          const packageMesh = this.createPackageMesh(color);
          packageMesh.position.copy(position);
          this.scene.add(packageMesh);

          const newPackage: Package = {
            id: serverPackage.id,
            mesh: packageMesh,
            position,
            isPickedUp: serverPackage.isPickedUp,
            endPosition,
            value: serverPackage.value,
            color,
            pickedUpBy: serverPackage.pickedUpBy,
            pickupTime: serverPackage.pickupTime,
            createdAt: serverPackage.createdAt,
            isBeingDelivered: false,
            isStolen: serverPackage.isStolen || false
          };

          // Create label for package if not picked up
          if (!newPackage.isPickedUp) {
            this.createPackageLabel(newPackage);
          }
          // Create destination label if picked up by local player and not stolen
          else if (newPackage.pickedUpBy === this.socket.id && !newPackage.isStolen) {
            this.createDestinationLabel(newPackage);
          }

          this.packages.set(serverPackage.id, newPackage);
        } else {
          // Update existing package
          const pkg = this.packages.get(serverPackage.id)!;
          const wasNotPickedUp = !pkg.isPickedUp;
          const wasNotStolen = !pkg.isStolen;
          
          pkg.isPickedUp = serverPackage.isPickedUp;
          pkg.pickedUpBy = serverPackage.pickedUpBy;
          pkg.pickupTime = serverPackage.pickupTime;
          pkg.isStolen = serverPackage.isStolen || false;
          
          if (pkg.isPickedUp) {
            pkg.mesh.visible = false;
            
            // Remove label if package is picked up
            if (pkg.labelElement && pkg.labelElement.parentNode) {
              pkg.labelElement.parentNode.removeChild(pkg.labelElement);
              pkg.labelElement = undefined;
            }
            
            // Create destination label if picked up by local player and doesn't have one and not stolen
            if (pkg.pickedUpBy === this.socket.id && !pkg.destinationLabelElement && !pkg.isStolen) {
              this.createDestinationLabel(pkg);
            }
            
            // Remove destination label if package is now stolen
            if (pkg.isStolen && pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
              pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
              pkg.destinationLabelElement = undefined;
            }
          } else {
            pkg.mesh.visible = true;
            
            // Create label if package is visible but doesn't have a label
            if (!pkg.labelElement) {
              this.createPackageLabel(pkg);
            }
            
            // Remove destination label if package is no longer picked up
            if (pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
              pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
              pkg.destinationLabelElement = undefined;
            }
          }
        }
      });

      // Update inventory display
      this.updateInventory();
    });

    // Listen for player updates (including confidence value)
    this.socket.on('packageUpdate', (data: any) => {
      if (data.id === this.socket.id) {
        // Si le joueur est local, nous mettons à jour les éléments d'interface utilisateur
        
        console.log('Received package update:', data);
        
        // Mettre à jour le score si fourni par le serveur
        if (data.score !== undefined) {
          this.playerScore = data.score;
        }
        
        // Update player confidence if provided by the server
        if (data.confidence !== undefined) {
          this.playerConfidence = data.confidence;
          this.updateConfidenceBar();
        }
        
        // Update thief status if provided
        if (data.thief !== undefined) {
          // Informer le PostmanController du changement de statut
          if (this.postmanController) {
            (this.postmanController as any).thief = data.thief;
          }
        }
        
        // Mettre à jour l'affichage du score
        this.updateScoreDisplay();
        
        // Afficher un feedback visuel en fonction du statut de livraison
        if (data.deliveryStatus) {
          switch (data.deliveryStatus) {
            case 'normal':
              // Livraison à temps avec bonus
              this.showDeliveryFeedback('success');
              break;
            case 'late':
              // Livraison en retard, sans bonus
              this.showDeliveryFeedback('late');
              break;
            case 'very_late':
              // Livraison très en retard (au-delà d'une minute)
              this.showDeliveryFeedback('very_late');
              break;
            case 'stolen':
              // Colis volé
              this.showDeliveryFeedback('stolen');
              break;
          }
        } else if (data.rewardEarned !== undefined) {
          // Compatibilité avec l'ancien format
          this.showDeliveryFeedback(data.rewardEarned ? 'success' : 'late');
        }
      }
    });
    
    // Écouter les événements d'immobilisation par la police
    this.socket.on('playerImmobilized', (data: any) => {
      if (data.id === this.socket.id) {
        // Mettre à jour le score si fourni par le serveur
        if (data.score !== undefined) {
          this.playerScore = data.score;
          this.updateScoreDisplay();
        }
        
        // Mettre à jour le statut de voleur si fourni
        if (data.thief !== undefined && this.postmanController) {
          (this.postmanController as any).thief = data.thief;
        }
        
        // Mettre à jour l'inventaire immédiatement
        this.updateInventory();
        
        // Afficher un message d'alerte pour indiquer les conséquences
        this.showDeliveryFeedback('police_penalty');
      }
    });
    
    // Listen for leaderboard updates
    this.socket.on('leaderboardUpdate', (players: { name: string; score: number }[]) => {
      this.updateLeaderboard(players);
    });
  }

  private createPackageMesh(color: number): THREE.Group {
    const group = new THREE.Group();
    
    // Create package box
    const boxGeometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    const boxMaterial = new THREE.MeshPhongMaterial({ 
      color: 0xf4a974, // Marron pour le colis au lieu de gris clair
      flatShading: true,
      shininess: 0
    });
    const box = new THREE.Mesh(boxGeometry, boxMaterial);
    group.add(box);
    
    // Add colored marking on top (matching destination color)
    const markerGeometry = new THREE.BoxGeometry(0.6, 0.05, 0.6);
    const markerMaterial = new THREE.MeshPhongMaterial({ 
      color,
      flatShading: true,
      shininess: 0
    });
    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.y = 0.425; // Half box height + half marker height
    group.add(marker);
    
    // Add subtle hover animation
    const hoverAnimation = () => {
      const time = Date.now() * 0.002;
      box.position.y = Math.sin(time) * 0.1;
      marker.position.y = 0.425 + Math.sin(time) * 0.1;
      
      requestAnimationFrame(hoverAnimation);
    };
    
    hoverAnimation();
    
    return group;
  }

  /**
   * Create a label for a package
   */
  private createPackageLabel(pkg: Package): void {
    // Create the label element
    const label = document.createElement('div');
    label.className = 'package-label';
    label.style.position = 'absolute';
    label.style.transform = 'translate(-50%, -50%)';
    label.style.backgroundColor = `#${pkg.color.toString(16).padStart(6, '0')}`;
    label.style.color = 'white';
    label.style.padding = '2px 6px';
    label.style.borderRadius = '50%';
    label.style.fontFamily = "'Nunito', sans-serif";
    label.style.fontSize = '12px';
    label.style.fontWeight = 'bold';
    label.style.textAlign = 'center';
    label.style.whiteSpace = 'nowrap';
    label.style.pointerEvents = 'none';
    label.style.border = '1px solid rgba(255, 255, 255, 0.7)';
    label.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.3)';
    label.style.zIndex = '900';
    label.textContent = 'P';
    
    // Ajouter un effet de reflet pour un style plus moderne
    const highlight = document.createElement('div');
    highlight.style.position = 'absolute';
    highlight.style.top = '10%';
    highlight.style.left = '10%';
    highlight.style.width = '30%';
    highlight.style.height = '30%';
    highlight.style.borderRadius = '50%';
    highlight.style.background = 'rgba(255, 255, 255, 0.4)';
    highlight.style.pointerEvents = 'none';
    label.appendChild(highlight);
    
    // Add to the container
    this.packageLabelsContainer.appendChild(label);
    
    // Store reference to the label in the package
    pkg.labelElement = label;
    
    // Initial position update
    this.updatePackageLabelPosition(pkg);
  }

  /**
   * Update a package label's position based on the package's 3D position
   */
  private updatePackageLabelPosition(pkg: Package): void {
    if (!pkg.labelElement) return;
    
    // Clone the position and add height offset (above the package)
    const offsetPosition = pkg.mesh.position.clone().add(new THREE.Vector3(0, 0.9, 0));
    
    // Project the 3D position to 2D screen space
    const screenPosition = this.worldToScreen(offsetPosition);
    
    // Only show if the position is in front of the camera
    if (screenPosition.z > 0) {
      pkg.labelElement.style.display = 'block';
      pkg.labelElement.style.left = `${screenPosition.x}px`;
      pkg.labelElement.style.top = `${screenPosition.y}px`;
      
      // Scale based on distance
      const distance = this.camera.position.distanceTo(pkg.mesh.position);
      
      // Hide if too far
      if (distance > 50) {
        pkg.labelElement.style.display = 'none';
      } else {
        // Adjust opacity based on distance - fade out as distance increases
        const opacity = Math.max(0.3, Math.min(1.0, 1.0 - (distance / 50)));
        pkg.labelElement.style.opacity = opacity.toString();
        
        // Ajuste la taille du texte en fonction de la distance
        const scale = Math.max(0.7, Math.min(1.0, 1.0 - (distance / 60)));
        pkg.labelElement.style.transform = `translate(-50%, -50%) scale(${scale})`;
      }
    } else {
      // Hide if behind camera
      pkg.labelElement.style.display = 'none';
    }
  }

  /**
   * Convert a 3D world position to 2D screen coordinates
   */
  private worldToScreen(position: THREE.Vector3): THREE.Vector3 {
    // Clone position for the projection
    const pos = position.clone();
    
    // Vérifier que la caméra est bien définie
    if (!this.camera) {
      return new THREE.Vector3(0, 0, -1); // Retourner une position hors écran
    }
    
    // Project position to screen space
    pos.project(this.camera);
    
    // Si le point est derrière la caméra ou trop loin, retourner une position hors écran
    if (pos.z > 1) {
      return new THREE.Vector3(0, 0, -1);
    }
    
    // Convert normalized device coordinates (-1 to 1) to pixel coordinates
    const halfWidth = window.innerWidth / 2;
    const halfHeight = window.innerHeight / 2;
    const x = Math.round((pos.x * halfWidth) + halfWidth);
    const y = Math.round(-(pos.y * halfHeight) + halfHeight);
    
    return new THREE.Vector3(x, y, pos.z);
  }

  /**
   * Check if camera has moved enough to warrant updating all labels
   */
  private hasCameraMovedSignificantly(): boolean {
    // Check position change
    const positionChanged = this.camera.position.distanceTo(this.lastCameraPosition) > this.updateThreshold;
    
    // Check rotation change (more sensitive for better responsiveness)
    const rotationChanged = 
      Math.abs(this.camera.rotation.x - this.lastCameraRotation.x) > 0.005 || 
      Math.abs(this.camera.rotation.y - this.lastCameraRotation.y) > 0.005 || 
      Math.abs(this.camera.rotation.z - this.lastCameraRotation.z) > 0.005;
    
    return positionChanged || rotationChanged;
  }

  /**
   * Update all package label positions
   */
  private updateAllPackageLabels(): void {
    // Vérifier si la caméra est en mode top-down ou 3rd person
    const isTopDown = this.camera.position.y > 30; // Approximation pour détecter le mode topdown
    
    for (const pkg of this.packages.values()) {
      // Update pickup labels for non-picked up packages
      if (!pkg.isPickedUp && pkg.labelElement) {
        // Ajuster la visibilité des étiquettes en fonction du mode de caméra
        if (isTopDown) {
          // En vue topdown, on affiche toujours les étiquettes à une hauteur fixe
          pkg.labelElement.style.display = 'block';
          
          // Ajuster la position pour la vue topdown
          const topDownPos = pkg.mesh.position.clone().add(new THREE.Vector3(0, 0.1, 0));
          const screenPos = this.worldToScreen(topDownPos);
          
          if (screenPos.z > 0) {
            pkg.labelElement.style.left = `${screenPos.x}px`;
            pkg.labelElement.style.top = `${screenPos.y}px`;
            pkg.labelElement.style.opacity = '1.0';
          } else {
            pkg.labelElement.style.display = 'none';
          }
        } else {
          // En vue normale, on utilise le positionnement standard
          this.updatePackageLabelPosition(pkg);
        }
      }
      
      // Update destination labels for packages picked up by the local player
      if (pkg.isPickedUp && pkg.pickedUpBy === this.socket.id && pkg.destinationLabelElement) {
        if (isTopDown) {
          // Always show in top-down view
          pkg.destinationLabelElement.style.display = 'block';
          
          // Adjust position for top-down view
          const topDownPos = pkg.endPosition.clone().add(new THREE.Vector3(0, 0.1, 0));
          const screenPos = this.worldToScreen(topDownPos);
          
          if (screenPos.z > 0) {
            pkg.destinationLabelElement.style.left = `${screenPos.x}px`;
            pkg.destinationLabelElement.style.top = `${screenPos.y}px`;
            pkg.destinationLabelElement.style.opacity = '1.0';
            
            // En vue top-down, on applique un scale fixe et plus grand
            pkg.destinationLabelElement.style.transform = 'translate(-50%, -50%) scale(1.2)';
          } else {
            pkg.destinationLabelElement.style.display = 'none';
          }
        } else {
          // Use standard positioning in normal view
          this.updateDestinationLabelPosition(pkg);
        }
      }
    }
    
    // Update last known camera position and rotation
    this.lastCameraPosition.copy(this.camera.position);
    this.lastCameraRotation.copy(this.camera.rotation);
  }

  public update(): void {
    // Check for package pickups
    for (const [id, pkg] of this.packages) {
      if (!pkg.isPickedUp) {
        const playerPosition = this.getPlayerPosition();
        if (playerPosition.distanceTo(pkg.mesh.position) < this.pickupRange) {
          this.pickupPackage(id);
        }
      }
    }

    // Check for package deliveries
    for (const [id, pkg] of this.packages) {
      if (pkg.isPickedUp && pkg.pickedUpBy === this.socket.id && !pkg.isBeingDelivered && !pkg.isStolen) {
        const playerPosition = this.getPlayerPosition();
        if (playerPosition.distanceTo(pkg.endPosition) < this.deliveryRange) {
          // Marquer le colis comme étant en cours de livraison pour éviter de redéclencher
          pkg.isBeingDelivered = true;
          this.deliverPackage(id);
        }
      }
    }
    
    // Update package labels if camera has moved significantly
    if (this.hasCameraMovedSignificantly()) {
      this.updateAllPackageLabels();
    }
  }

  private getPlayerPosition(): THREE.Vector3 {
    return this.postmanController.getPosition();
  }

  private pickupPackage(packageId: string): void {
    this.socket.emit('packagePickup', packageId);
    
    // Play pickup sound effect
    this.playSound('pickup');
  }

  private deliverPackage(packageId: string): void {
    const pkg = this.packages.get(packageId);
    if (!pkg) return;
    
    // Vérifie que le joueur est bien dans la zone de livraison
    const playerPosition = this.getPlayerPosition();
    if (playerPosition.distanceTo(pkg.endPosition) > this.deliveryRange) return;
    
    // Désactive les contrôles du joueur pendant la livraison
    const postmanController = this.postmanController as any;
    if (postmanController.disableControls) {
      postmanController.disableControls();
    }
    
    // Créer la barre de défilement
    const progressBarContainer = this.createDeliveryProgressBar(pkg.color);
    
    // Timer de 3 secondes pour la livraison
    let progress = 0;
    const deliveryTime = 3000; // 3 secondes
    const startTime = Date.now();
    const progressBar = progressBarContainer.querySelector('.progress-bar') as HTMLElement;
    
    // Ajouter bouton de vol sur mobile
    let stealButton: HTMLElement | null = null;
    if (this.isMobile()) {
      stealButton = document.createElement('button');
      stealButton.textContent = ' STEAL ';
      stealButton.style.position = 'absolute';
      stealButton.style.bottom = '-100px';
      stealButton.style.left = '50%';
      stealButton.style.transform = 'translateX(-50%)';
      stealButton.style.padding = '12px 25px';
      stealButton.style.backgroundColor = '#FF0000';
      stealButton.style.color = 'white';
      stealButton.style.border = 'none';
      stealButton.style.borderRadius = '5px';
      stealButton.style.fontWeight = 'bold';
      stealButton.style.fontSize = '18px';
      stealButton.style.textShadow = '1px 1px 2px rgba(0, 0, 0, 0.5)';
      stealButton.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
      stealButton.style.cursor = 'pointer';
      progressBarContainer.appendChild(stealButton);
      
      // Handler pour le bouton de vol
      stealButton.addEventListener('click', () => {
        stealPackage();
      });
    }
    
    // Fonction pour gérer le vol du colis
    const stealPackage = () => {
      // Arrêter l'animation
      cancelAnimationFrame(animationId);
      
      // Supprimer la barre de progression
      document.body.removeChild(progressBarContainer);
      
      // Marquer le colis comme volé
      this.markPackageAsStolen(packageId);
      
      // Réactiver les contrôles du joueur
      if (postmanController.enableControls) {
        postmanController.enableControls();
      }
    };
    
    // Handler pour la touche E pour voler le colis
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'e' || event.key === 'E') {
        stealPackage();
        document.removeEventListener('keydown', keyDownHandler);
      }
    };
    
    // Ajouter l'écouteur d'événement pour la touche E
    document.addEventListener('keydown', keyDownHandler);
    
    // Animation de la barre de progression
    let animationId: number;
    const updateProgressBar = () => {
      const elapsed = Date.now() - startTime;
      progress = Math.min(elapsed / deliveryTime, 1);
      
      if (progressBar) {
        progressBar.style.width = `${progress * 100}%`;
      }
      
      if (progress < 1) {
        animationId = requestAnimationFrame(updateProgressBar);
      } else {
        // La barre de progression est complète, livrer le colis
        document.body.removeChild(progressBarContainer);
        document.removeEventListener('keydown', keyDownHandler);
        
        // Envoyer la demande de livraison au serveur
        this.socket.emit('packageDelivery', packageId);
        
        // Play delivery sound effect
        this.playSound('delivery');
        
        // Create a visual effect at the delivery point
        // Remove the destination label
        if (pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
          pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
          pkg.destinationLabelElement = undefined;
        }
        
        this.createDeliveryEffect(pkg.endPosition.clone(), pkg.color);
        
        // Réactiver les contrôles du joueur
        if (postmanController.enableControls) {
          postmanController.enableControls();
        }
      }
    };
    
    // Démarrer l'animation
    animationId = requestAnimationFrame(updateProgressBar);
  }
  
  // Créer une barre de progression pour la livraison
  private createDeliveryProgressBar(color: number): HTMLElement {
    const isMobile = this.isMobile();
    
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.width = isMobile ? '250px' : '300px';
    container.style.height = isMobile ? '25px' : '30px';
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    container.style.borderRadius = '15px';
    container.style.padding = '5px';
    container.style.zIndex = '1000';
    
    const label = document.createElement('div');
    label.textContent = 'DELIVERING';
    label.style.position = 'absolute';
    label.style.top = isMobile ? '-25px' : '-30px';
    label.style.left = '50%';
    label.style.transform = 'translateX(-50%)';
    label.style.color = 'white';
    label.style.fontFamily = 'Arial, sans-serif';
    label.style.fontSize = isMobile ? '14px' : '16px';
    label.style.fontWeight = 'bold';
    container.appendChild(label);
    
    // Ajout d'une instruction indiquant comment voler le colis
    const stealInstruction = document.createElement('div');
    stealInstruction.textContent = this.isMobile() ? 'Use button below to steal' : 'Press E to steal';
    stealInstruction.style.position = 'absolute';
    stealInstruction.style.bottom = isMobile ? '-22px' : '-25px';
    stealInstruction.style.left = '50%';
    stealInstruction.style.transform = 'translateX(-50%)';
    stealInstruction.style.color = '#ff5a5a'; // Rouge pour suggérer une action négative
    stealInstruction.style.fontFamily = 'Arial, sans-serif';
    stealInstruction.style.fontSize = isMobile ? '12px' : '14px';
    stealInstruction.style.fontWeight = 'bold';
    container.appendChild(stealInstruction);
    
    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar';
    progressBar.style.width = '0%';
    progressBar.style.height = '100%';
    progressBar.style.backgroundColor = `#${color.toString(16).padStart(6, '0')}`;
    progressBar.style.borderRadius = '10px';
    progressBar.style.transition = 'width 0.1s linear';
    container.appendChild(progressBar);
    
    document.body.appendChild(container);
    return container;
  }
  
  // Marquer un colis comme volé
  private markPackageAsStolen(packageId: string): void {
    const pkg = this.packages.get(packageId);
    if (!pkg) return;
    
    // Marquer le colis comme volé
    pkg.isStolen = true;
    
    // Informer le serveur que le colis a été volé
    this.socket.emit('packageStolen', packageId);
    
    // Mettre à jour l'interface pour montrer que le colis est volé
    // Supprimer l'indicateur de destination
    if (pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
      pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
      pkg.destinationLabelElement = undefined;
    }
    
    // Mettre à jour immédiatement l'affichage de l'inventaire
    this.updateInventory();
    
    // Afficher un message de feedback
    this.showDeliveryFeedback('stolen');
  }
  
  // Détection mobile
  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private playSound(type: 'pickup' | 'delivery'): void {
    // Implement sound effects here if needed
  }

  private startInventoryUpdate(): void {
    // Update immediately
    this.updateInventory();
    
    // Then update every second
    this.inventoryUpdateInterval = window.setInterval(() => {
      this.updateInventory();
    }, 1000);
  }

  public cleanup(): void {
    if (this.inventoryUpdateInterval !== null) {
      clearInterval(this.inventoryUpdateInterval);
      this.inventoryUpdateInterval = null;
    }
    
    // Remove all package labels
    for (const pkg of this.packages.values()) {
      if (pkg.labelElement && pkg.labelElement.parentNode) {
        pkg.labelElement.parentNode.removeChild(pkg.labelElement);
      }
      if (pkg.destinationLabelElement && pkg.destinationLabelElement.parentNode) {
        pkg.destinationLabelElement.parentNode.removeChild(pkg.destinationLabelElement);
      }
    }
  }

  /**
   * Create a destination label for a package
   */
  private createDestinationLabel(pkg: Package): void {
    // Create the label element
    const label = document.createElement('div');
    label.className = 'destination-label';
    label.style.position = 'absolute';
    label.style.transform = 'translate(-50%, -50%)';
    label.style.backgroundColor = `#${pkg.color.toString(16).padStart(6, '0')}`;
    label.style.color = 'white';
    label.style.padding = '4px 8px'; // Augmentation de la taille du padding
    label.style.borderRadius = '50%';
    label.style.fontFamily = "'Nunito', sans-serif";
    label.style.fontSize = '16px'; // Augmentation de la taille de police
    label.style.fontWeight = 'bold';
    label.style.textAlign = 'center';
    label.style.whiteSpace = 'nowrap';
    label.style.pointerEvents = 'none';
    label.style.border = '2px solid rgba(255, 255, 255, 0.8)'; // Bordure plus visible
    label.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.5)'; // Ombre plus prononcée
    label.style.zIndex = '950'; // Priorité plus élevée que les autres labels
    label.textContent = 'D';
    
    // Add highlight effect for a modern look
    const highlight = document.createElement('div');
    highlight.style.position = 'absolute';
    highlight.style.top = '10%';
    highlight.style.left = '10%';
    highlight.style.width = '30%';
    highlight.style.height = '30%';
    highlight.style.borderRadius = '50%';
    highlight.style.background = 'rgba(255, 255, 255, 0.4)';
    highlight.style.pointerEvents = 'none';
    label.appendChild(highlight);
    
    // Add to the container
    this.packageLabelsContainer.appendChild(label);
    
    // Store reference to the label in the package
    pkg.destinationLabelElement = label;
    
    // Initial position update
    this.updateDestinationLabelPosition(pkg);
  }

  /**
   * Update a destination label's position based on the package's endPosition
   */
  private updateDestinationLabelPosition(pkg: Package): void {
    if (!pkg.destinationLabelElement) return;
    
    // Clone the end position and add height offset (slightly above ground)
    const offsetPosition = pkg.endPosition.clone().add(new THREE.Vector3(0, 0.9, 0));
    
    // Project the 3D position to 2D screen space
    const screenPosition = this.worldToScreen(offsetPosition);
    
    // Always show regardless of distance, only hide if behind camera
    if (screenPosition.z > 0) {
      pkg.destinationLabelElement.style.display = 'block';
      pkg.destinationLabelElement.style.left = `${screenPosition.x}px`;
      pkg.destinationLabelElement.style.top = `${screenPosition.y}px`;
      
      // Rendre le label toujours visible mais faire varier légèrement la taille selon la distance
      const distance = this.camera.position.distanceTo(pkg.endPosition);
      
      // Toujours visible même à grande distance
      pkg.destinationLabelElement.style.opacity = '1.0';
      
      // Ajuster la taille en fonction de la distance, mais maintenir une taille minimale
      // pour que ça reste toujours bien visible
      const minScale = 0.9; // Scale minimum pour garantir la visibilité
      const scale = Math.max(minScale, 1.5 - (distance / 150)); // Réduction plus lente avec la distance
      pkg.destinationLabelElement.style.transform = `translate(-50%, -50%) scale(${scale})`;
      
      // Ajouter un effet de pulsation pour les destinations très éloignées
      if (distance > 80) {
        const pulseFrequency = 0.8; // Plus lent pour être moins distrayant
        const currentTime = Date.now() * 0.001;
        const pulseEffect = 0.1 * Math.sin(currentTime * pulseFrequency * Math.PI) + 1.0;
        pkg.destinationLabelElement.style.transform = `translate(-50%, -50%) scale(${scale * pulseEffect})`;
      }
    } else {
      // Hide if behind camera
      pkg.destinationLabelElement.style.display = 'none';
    }
  }

  // Method to update the score display
  private updateScoreDisplay(): void {
    if (this.scoreElement) {
      this.scoreElement.textContent = `$ : ${this.playerScore}`;
    }
  }

  private showDeliveryFeedback(status: string): void {
    // Create a notification element
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.top = '50%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.padding = '15px 25px';
    notification.style.borderRadius = '8px';
    notification.style.color = 'white';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '24px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease-in-out';
    notification.style.pointerEvents = 'none'; // Don't block interactions

    if (status === 'success') {
      // Style for on-time delivery
      notification.style.backgroundColor = 'rgba(50, 205, 50, 0.9)'; // Semi-transparent green
      notification.textContent = 'Well done! 10% delivery reward';
    } else if (status === 'late') {
      // Style for late delivery
      notification.style.backgroundColor = 'rgba(220, 20, 60, 0.9)'; // Semi-transparent red
      notification.textContent = 'Too late! No delivery reward';
    } else if (status === 'very_late') {
      // Style for very late delivery (beyond 1 minute)
      notification.style.backgroundColor = 'rgba(220, 20, 60, 0.9)'; // Semi-transparent red
      notification.textContent = 'Very late! No delivery reward';
    } else if (status === 'stolen') {
      // Style for stolen packages
      notification.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'; // Semi-transparent black
      notification.textContent = "You've stolen a package. Sell it at the Black Market";
    } else if (status === 'police_penalty') {
      // Style for score loss due to police immobilization
      notification.style.backgroundColor = 'rgba(220, 20, 60, 0.9)'; // Semi-transparent red
      notification.textContent = "You're under arrest!";
    }

    // Add to DOM
    document.body.appendChild(notification);

    // Fade-in animation
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);

    // Fade out after 2 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      // Remove from DOM after fade-out animation
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300); // Duration of opacity transition
    }, 2000);
  }

  private createDeliveryEffect(position: THREE.Vector3, color: number): void {
    // Create pop-up effect
    const particles = new THREE.Group();
    const particleCount = 8;
    
    for (let i = 0; i < particleCount; i++) {
      const particleGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
      const particleMaterial = new THREE.MeshPhongMaterial({ 
        color,
        flatShading: true,
        shininess: 0
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      const angle = (i / particleCount) * Math.PI * 2;
      
      particles.add(particle);
      
      // Animate the particle outward
      const animateParticle = () => {
        const radius = 0.5 + (Date.now() % 1000) / 250;
        particle.position.set(
          Math.cos(angle) * radius, 
          0.5 + radius / 2, 
          Math.sin(angle) * radius
        );
        
        particle.scale.multiplyScalar(0.98);
        
        if (particle.scale.x > 0.01) {
          requestAnimationFrame(animateParticle);
        } else {
          particles.remove(particle);
          if (particles.children.length === 0) {
            this.scene.remove(particles);
          }
        }
      };
      
      animateParticle();
    }
    
    particles.position.copy(position);
    this.scene.add(particles);
  }

  // Check if player has stolen packages
  public hasStolenPackages(): boolean {
    let hasStolen = false;
    
    this.packages.forEach((pkg) => {
      if (pkg.isPickedUp && pkg.isStolen) {
        hasStolen = true;
      }
    });
    
    return hasStolen;
  }

  // Method to update the confidence bar
  private updateConfidenceBar(): void {
    const progressBar = this.confidenceBarElement.querySelector('#confidence-progress') as HTMLElement;
    if (progressBar) {
      // Set width based on confidence (0-1)
      const width = Math.max(0, Math.min(100, this.playerConfidence * 100));
      progressBar.style.width = `${width}%`;
      
      // Change color based on confidence level
      if (this.playerConfidence > 0.7) {
        // High confidence - green
        progressBar.style.backgroundColor = '#4CAF50';
      } else if (this.playerConfidence > 0.3) {
        // Medium confidence - yellow
        progressBar.style.backgroundColor = '#FFC107';
      } else {
        // Low confidence - red
        progressBar.style.backgroundColor = '#F44336';
      }
    }
  }

  // Update leaderboard with new data from server
  private updateLeaderboard(players: { name: string; score: number }[]): void {
    const listElement = this.leaderboardElement.querySelector('#leaderboard-list');
    if (!listElement) return;
    
    // Clear existing entries
    listElement.innerHTML = '';
    
    // Add new entries
    players.forEach((player, index) => {
      const listItem = document.createElement('li');
      
      // Add rank
      const rankSpan = document.createElement('span');
      rankSpan.className = 'player-rank';
      rankSpan.textContent = `#${index + 1}`;
      listItem.appendChild(rankSpan);
      
      // Add player name
      const nameSpan = document.createElement('span');
      nameSpan.className = 'player-name';
      nameSpan.textContent = player.name;
      listItem.appendChild(nameSpan);
      
      // Add score
      const scoreSpan = document.createElement('span');
      scoreSpan.className = 'player-score';
      scoreSpan.textContent = `$${player.score}`;
      listItem.appendChild(scoreSpan);
      
      // Style the list item
      listItem.style.display = 'flex';
      listItem.style.justifyContent = 'space-between';
      listItem.style.padding = '4px 0';
      listItem.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      
      // Add to list
      listElement.appendChild(listItem);
    });
    
    // Add empty entries if less than 10 players
    const emptyNeeded = 10 - players.length;
    for (let i = 0; i < emptyNeeded; i++) {
      const listItem = document.createElement('li');
      
      // Add rank
      const rankSpan = document.createElement('span');
      rankSpan.className = 'player-rank';
      rankSpan.textContent = `#${players.length + i + 1}`;
      rankSpan.style.opacity = '0.5';
      listItem.appendChild(rankSpan);
      
      // Add placeholder for name
      const nameSpan = document.createElement('span');
      nameSpan.className = 'player-name';
      nameSpan.textContent = '—';
      nameSpan.style.opacity = '0.5';
      listItem.appendChild(nameSpan);
      
      // Add placeholder for score
      const scoreSpan = document.createElement('span');
      scoreSpan.className = 'player-score';
      scoreSpan.textContent = '0';
      scoreSpan.style.opacity = '0.5';
      listItem.appendChild(scoreSpan);
      
      // Style the list item
      listItem.style.display = 'flex';
      listItem.style.justifyContent = 'space-between';
      listItem.style.padding = '4px 0';
      listItem.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      listItem.style.opacity = '0.5';
      
      // Add to list
      listElement.appendChild(listItem);
    }
  }
} 